import { TrustCheckCard } from "@/components/features/matter/trust-checks/TrustCheckCard";
import { Card, CardHeader, CurrencyWrapper, Text } from "@/components/ui";
import { useTrustChecksViewModel } from "@/hooks/matter-view-models/use-trust-checks-view-model";
import { type Matter } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import dayjs from "dayjs";

interface TrustLedgerCardProps {
  matter: Matter;
}

export const TrustLedgerCard = ({ matter }: TrustLedgerCardProps) => {
  const matterId = matter.id;
  const trustChecksViewModel = useTrustChecksViewModel(matterId);

  // sort by date
  const sortedTrustChecksViewModel = trustChecksViewModel.sort((a, b) =>
    dayjs(b.dateReceived).diff(dayjs(a.dateReceived)),
  );

  // Calculate running total
  const runningTotal = sortedTrustChecksViewModel.reduce(
    (total, trustCheck) => {
      const amount = trustCheck.amount ?? 0;
      if (trustCheck.type === "Deposit") {
        return total + amount;
      } else {
        return total - amount;
      }
    },
    0,
  );

  return (
    <Card className="flex w-full flex-col gap-4 border p-4">
      {trustChecksViewModel.length === 0 ? (
        <Card className="w-full border-2 border-dashed">
          <CardHeader className="space-y-1 text-center">
            <Text
              variant="body"
              className="text-muted-foreground font-semibold"
            >
              You haven&apos;t added any trust checks yet.
            </Text>
            <Text variant="subHeading">
              Create your first in the settlements tab.
            </Text>
          </CardHeader>
        </Card>
      ) : (
        <>
          {/* Running Total */}
          <div className="border-border flex items-center justify-between border-b pb-4">
            <Text
              variant="body"
              color="primary"
              className="text-lg font-semibold"
            >
              Trust ledger balance
            </Text>
            <Text
              variant="title"
              className={cn(
                "text-2xl leading-8",
                runningTotal >= 0 ? "text-green-600" : "text-red-600",
              )}
            >
              <CurrencyWrapper value={runningTotal} />
            </Text>
          </div>

          {/* Trust Check List */}
          <div className="grid grid-cols-1 gap-2">
            {sortedTrustChecksViewModel.map((trustCheck) => (
              <TrustCheckCard
                key={trustCheck.id}
                trustCheck={trustCheck}
                matter={matter}
                showConnections={true}
                className="border-border/50 rounded-none border-0 border-b shadow-none"
              />
            ))}
          </div>
        </>
      )}
    </Card>
  );
};
